"use client";

import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CandidateExperience, CandidateProfile } from "@/lib/api/types";
import { formatYearMonthShort } from "@/lib/date/date";
import { cn } from "@/lib/utils";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import { useCompanyDetailModal } from "@/hooks/use-company-detail-modal";
import { CompanyLogo } from "@/features/candidate";
import { ExperienceRoleSummary } from "./experience-role-summary";
import { SectionCard } from "../section-layout";
import {
  computeDuration,
  formatMonths,
  groupExperiencesByCompany,
} from "@/features/candidate/lib/profile-utils";
import { Icon } from "@/components/ui/icon";
import { HighlightedText } from "@/features/arbitrator/components/review/highlighted-text";

/** Figma 799:1692 — Promotion pill */
function PromotionBadge() {
  return (
    <div className="flex shrink-0 items-center justify-center overflow-clip rounded-[5.4px] border-[0.9px] border-solid border-[#1a6b35]/20 bg-[#1a6b35]/8 dark:border-[rgba(99,246,143,0.1)] dark:bg-[rgba(99,246,143,0.05)] p-0.5">
      <div className="flex items-center px-1 py-0">
        <span className="whitespace-nowrap text-[10.8px] font-normal leading-[14.4px] text-[#1a6b35] dark:text-[#63f68f]">
          Promotion
        </span>
      </div>
    </div>
  );
}

function TenureCards({
  avgMonths,
  currentMonths,
  totalMonths,
}: {
  avgMonths: number | null;
  currentMonths: number | null;
  totalMonths: number | null;
}) {
  const cards = [
    {
      label: "Average Tenure",
      value: avgMonths != null ? formatMonths(Math.round(avgMonths)) : "—",
    },
    {
      label: "Current Tenure",
      value: currentMonths != null ? formatMonths(currentMonths) : "—",
    },
    {
      label: "Total Experience",
      value: totalMonths != null ? formatMonths(totalMonths) : "—",
    },
  ];

  return (
    <div className="flex w-full flex-wrap items-start gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="relative h-[68px] min-h-[68px] min-w-0 flex-1 basis-[calc(50%-0.5rem)] overflow-hidden rounded-xl border border-border sm:basis-0"
        >
          <div className="flex h-[26px] items-center justify-center bg-surface-header px-page">
            <p className="whitespace-normal text-xs font-medium leading-normal text-foreground/80 sm:whitespace-nowrap">
              {card.label}
            </p>
          </div>
          <div className="absolute inset-x-0 bottom-0 flex h-[42px] items-center justify-center border-t border-border bg-surface-elevated">
            <p className="whitespace-nowrap text-[15px] font-medium leading-normal text-foreground">
              {card.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function RoleDetails({
  role,
  showPromotion,
  experienceIndex,
}: {
  role: ReturnType<typeof groupExperiencesByCompany>[0]["roles"][0];
  showPromotion: boolean;
  experienceIndex?: number;
}) {
  return (
    <div
      className="min-w-0 flex flex-col gap-1 items-start"
      data-evidence-source="experience"
      data-evidence-index={experienceIndex}
    >
      <div className="flex flex-wrap items-start gap-1">
        <p className="text-sm font-medium leading-5 text-foreground tracking-[-0.14px]">
          <HighlightedText source="experience">
            {titleCasePreserveAcronyms(role.title_name ?? "")}
          </HighlightedText>
        </p>
        {showPromotion && <PromotionBadge />}
      </div>
      <p className="text-[13px] font-normal text-foreground/70 tracking-[-0.13px]">
        {role.start_date && formatYearMonthShort(role.start_date)}
        {role.start_date && " - "}
        {role.end_date
          ? formatYearMonthShort(role.end_date)
          : role.start_date
            ? "Present"
            : ""}
        {(() => {
          const dur = computeDuration(role.start_date, role.end_date);
          return dur ? <> ({dur})</> : null;
        })()}
        {role.company_location_name && (
          <>
            <span className="text-foreground/25"> · </span>
            {titleCasePreserveAcronyms(role.company_location_name)}
          </>
        )}
      </p>
      {role.summary && <ExperienceRoleSummary summary={role.summary} />}
    </div>
  );
}

function ExperienceCompanyGroup({
  group,
  startIndex,
}: {
  group: ReturnType<typeof groupExperiencesByCompany>[0];
  startIndex: number;
}) {
  const { openCompanyDetail } = useCompanyDetailModal();
  const handleCompanyClick = useCallback(() => {
    if (group.companyId) openCompanyDetail(group.companyId);
  }, [group.companyId, openCompanyDetail]);
  /**
   * When there's no linked organization, we drop the empty company header
   * and inline the first role next to the placeholder logo. The timeline
   * line + dots only appear if there's more than one role to connect.
   */
  const hasCompany = group.companyName.trim().length > 0;

  const totalDuration = useMemo(() => {
    let minStart: Date | null = null;
    let maxEnd: Date | null = null;
    for (const r of group.roles) {
      if (r.start_date) {
        const d = new Date(r.start_date);
        if (!minStart || d < minStart) minStart = d;
      }
      const ed = r.end_date
        ? new Date(r.end_date)
        : r.start_date
          ? new Date()
          : null;
      if (ed) {
        if (!maxEnd || ed > maxEnd) maxEnd = ed;
      }
    }
    if (!minStart || !maxEnd) return null;
    const months =
      (maxEnd.getFullYear() - minStart.getFullYear()) * 12 +
      (maxEnd.getMonth() - minStart.getMonth());
    return formatMonths(Math.max(0, months));
  }, [group.roles]);

  const roles = group.roles;
  const showPromotionOnIndex = (idx: number) => idx === 0 && roles.length > 1;
  /** In the no-company layout the first role sits next to the logo, so the timeline dots only cover the remaining roles. */
  const dotRoles = hasCompany ? roles : roles.slice(1);
  const showTimeline = dotRoles.length > 0;

  const containerRef = useRef<HTMLDivElement>(null);
  const lastDotRef = useRef<HTMLDivElement>(null);
  const [lineHeightPx, setLineHeightPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const lastDot = lastDotRef.current;
      if (!container || !lastDot || !showTimeline) {
        setLineHeightPx(null);
        return;
      }
      const cRect = container.getBoundingClientRect();
      const dotRect = lastDot.getBoundingClientRect();
      const dotCenterY = dotRect.top + dotRect.height / 2 - cRect.top;
      setLineHeightPx(Math.max(0, dotCenterY - 42));
    };
    measure();
    const el = containerRef.current;
    const ro = new ResizeObserver(measure);
    if (el) ro.observe(el);
    return () => ro.disconnect();
  }, [showTimeline, dotRoles.length]);

  return (
    <div ref={containerRef} className="relative isolate w-full overflow-x-clip">
      {showTimeline && lineHeightPx != null && (
        <div
          className="pointer-events-none absolute left-5.25 top-10.5 w-px -translate-x-1/2 bg-border"
          style={{ height: lineHeightPx }}
          aria-hidden
        />
      )}
      <div className="relative grid w-full grid-cols-[42px_1fr] items-start gap-x-3 gap-y-4">
        {hasCompany ? (
          <>
            <button
              type="button"
              className="cursor-pointer hover:opacity-50 duration-200"
              onClick={handleCompanyClick}
            >
              <CompanyLogo
                size="lg"
                domain={group.companyDomain}
                name={group.companyName}
              />
            </button>
            <div className="min-w-0 flex flex-col items-start justify-center text-left rounded-md">
              <button
                type="button"
                className="cursor-pointer text-left hover:underline duration-200"
                onClick={handleCompanyClick}
              >
                {titleCasePreserveAcronyms(group.companyName)}
              </button>
              <div className="flex flex-wrap items-center gap-2">
                {group.companyIndustry && (
                  <div className="flex gap-1 items-center">
                    <Icon
                      name="IconBuildings"
                      size={13}
                      className="text-foreground/60"
                    />
                    <span className="text-[13px] font-normal text-foreground/80 tracking-[-0.13px]">
                      {titleCasePreserveAcronyms(group.companyIndustry)}
                    </span>
                  </div>
                )}
                {group.companyFounded && (
                  <>
                    <span className="text-foreground/25">·</span>
                    <div className="flex gap-1 items-center">
                      <Icon
                        name="IconPush"
                        size={13}
                        className="text-foreground/60"
                      />
                      <span className="text-[13px] font-normal text-foreground/80 tracking-[-0.13px]">
                        Founded in {group.companyFounded}
                      </span>
                    </div>
                  </>
                )}
                {group.companySize && (
                  <>
                    <span className="text-foreground/25">·</span>
                    <div className="flex gap-1 items-center">
                      <Icon
                        name="IconGroup1"
                        size={13}
                        className="text-foreground/60"
                      />
                      <span className="text-[13px] font-normal text-foreground/80 tracking-[-0.13px]">
                        {group.companySize} Headcount
                      </span>
                    </div>
                  </>
                )}
              </div>
              {totalDuration && (
                <p className="text-[13px] font-normal text-foreground/70 tracking-[-0.13px]">
                  <span>Full-time</span>
                  <span className="text-foreground/25"> · </span>
                  <span>{totalDuration}</span>
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <CompanyLogo size="lg" domain={null} name={null} />
            <RoleDetails
              role={roles[0]}
              showPromotion={showPromotionOnIndex(0)}
              experienceIndex={startIndex}
            />
          </>
        )}

        {dotRoles.map((role, dotIdx) => {
          const idx = hasCompany ? dotIdx : dotIdx + 1;
          return (
            <Fragment
              key={`${role.start_date ?? ""}-${role.title_name ?? ""}-${idx}`}
            >
              <div className="relative flex justify-center">
                <div
                  ref={dotIdx === dotRoles.length - 1 ? lastDotRef : undefined}
                  className="relative mt-1.5 size-2 shrink-0 rounded-full bg-muted-foreground"
                />
              </div>
              <RoleDetails
                role={role}
                showPromotion={showPromotionOnIndex(idx)}
                experienceIndex={startIndex + idx}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function ExperienceSection({ profile }: { profile: CandidateProfile }) {
  const companyGroups = useMemo(
    () =>
      groupExperiencesByCompany(
        (profile.experiences ?? []) as unknown as CandidateExperience[],
      ),
    [profile.experiences],
  );
  if (companyGroups.length === 0) return null;

  return (
    <SectionCard title="Experience">
      {/* Figma 799:1577 — tenure band (subtle bg) */}
      <div className="flex w-full min-w-0 shrink-0 flex-col items-stretch border-x border-t border-border bg-contrast/[0.02] p-5">
        <TenureCards
          avgMonths={profile.avg_tenure_months ?? null}
          currentMonths={profile.current_role_months ?? null}
          totalMonths={
            profile.total_experience_months ??
            (profile.total_experience_years != null
              ? Math.round(profile.total_experience_years * 12)
              : null)
          }
        />
      </div>
      {/* Figma 799:1606 — divider between tenure and company blocks */}
      <div className="h-px w-full shrink-0 bg-contrast/15" aria-hidden />
      {companyGroups.map((group, idx) => {
        const startIndex = companyGroups
          .slice(0, idx)
          .reduce((sum, g) => sum + g.roles.length, 0);
        return (
          <div key={`${group.companyName}-${idx}`} className="w-full min-w-0">
            {idx > 0 && <div className="h-px w-full bg-border" />}
            <div
              className={cn(
                "flex w-full min-w-0 shrink-0 flex-col items-stretch border-x border-border bg-surface-elevated p-5",
                idx === companyGroups.length - 1 &&
                  "rounded-b-md border-b border-border",
              )}
            >
              <ExperienceCompanyGroup group={group} startIndex={startIndex} />
            </div>
          </div>
        );
      })}
    </SectionCard>
  );
}
